import {
  BIT_VALUE,
  DATE_FORMAT,
  DEVICE_NAME,
  OS_NAME,
} from "./AppEnumerations";
import { TDateFormat } from "../data/AppType";
import CryptoJS from "crypto-js";
import { localStorageUtils } from "./LocalStorageUtil";
import { FormikErrors, FormikTouched } from "formik";
import {
  IStatusValues,
  IItemPagination,
  IPaginationApi,
  IOption,
} from "../data/AppInterface";
import {
  CURRENCY_SIGN,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
  MONTH_SHORT_NAME,
} from "./AppConstants";
import { CRYPTO_CIPHER_KEY } from "../config/AppConfig";
import ICToastMessage from "../core-component/ui/ICToastMessage";
import ICAnimatedAlert, {
  ICAnimatedAlertProps,
  ISweetAlertOptions,
} from "../core-component/ui/ICAnimatedAlert";
import { createRoot } from "react-dom/client";
import React from "react";
import { logApplicationError } from "../core-component/error-boundary/ErrorBoundaryController";

// Region Date And Time

export const convertUTCToTime = (
  utcTime: Date,
  offset: number,
  format?: TDateFormat
) => {
  const newTime = new Date(utcTime.getTime() + offset * 60 * 1000);
  if (format) {
    return dateFormat(newTime);
  }
  return newTime;
};
export const getLocalDate = (objPar?: string | number | Date) => {
  if (objPar) {
    return new Date(objPar);
  } else {
    return new Date();
  }
};

export const responseDateStringToDate = (
  dateString: string | undefined
): Date => {
  if (dateString) {
    const [day, month, year] = dateString.split("/");
    const newDate = getLocalDate(`${year}/${month}/${day}`);
    return newDate;
  } else {
    return getLocalDate();
  }
};

export const dateFormat = (
  date: Date | string,
  format: TDateFormat = DATE_FORMAT["DD-Mon-YYYY"]
) => {
  if (!(date instanceof Date)) {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) {
      return "";
    }
    date = parsedDate;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  switch (format) {
    case DATE_FORMAT["DD-MM-YYYY"]:
      return `${day}-${month}-${year}`;
    case DATE_FORMAT["MM-DD-YYYY"]:
      return `${month}-${day}-${year}`;
    case DATE_FORMAT["DD-Mon-YYYY"]:
      return `${day}-${MONTH_SHORT_NAME[date.getMonth()]}-${year}`;
    case DATE_FORMAT["YYYY-MM-DD"]:
      return `${year}-${month}-${day}`;
    default:
      return ""; // Return empty string for unsupported formats
  }
};

// time format
export const timeFormat = (date: Date): string => {
  const hours = date.getHours();
  const formattedHours = String(hours % 12 || 12).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const amPm = hours >= 12 ? "PM" : "AM";

  return `${formattedHours}:${minutes} ${amPm}`;
};
export const getLocalTime = (timeString?: string): Date | undefined => {
  if (timeString) {
    const [timePart] = timeString.split("+");

    const date = new Date(`2000-01-01T${timePart}`);

    return date;
  } else {
    return undefined;
  }
};

// Region Currency

export default function formatMoneyWithExchange(
  amount: number,
  targetCurrency?: string
): string {
  const formatter = new Intl.NumberFormat("USD", {
    style: "currency",
    currency: "USD",
  });

  return formatter.format(amount);
}

// end currency

//region phone format

export function formatPhoneNumber(
  phoneNumber: string,
  countryCode: string = "US"
): string {
  switch (countryCode) {
    case "US":
      // Format US phone number as (XXX) XXX-XXXX
      return phoneNumber.replace(/^(\d{3})(\d{3})(\d{4})$/, "($1) $2-$3");
    case "IN":
      // Format India phone number as +91 XXXXX-XXXXX
      return phoneNumber.replace(/^(\d{5})(\d{5})$/, "+91 $1-$2");
    case "CA":
      // Format Canada phone number as XXX-XXX-XXXX
      return phoneNumber.replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");
    default:
      return phoneNumber; // Return unformatted number if country code is not recognized
  }
}

// region end phone format

// Region   Tost Message

const toastSuccess = (
  message: string,
  duration?: number,
  position?: string
) => {
  ICToastMessage("success", message, duration, position);
};

const toastError = (message: string, duration?: number, position?: string) => {
  ICToastMessage("error", message, duration, position);
};

const toastWarning = (
  message: string,
  duration?: number,
  position?: string
) => {
  ICToastMessage("warning", message, duration, position);
};

const toastInfo = (message: string, duration?: number, position?: string) => {
  ICToastMessage("info", message, duration, position);
};

export { toastSuccess, toastError, toastWarning, toastInfo };

// Region End TostMessage

// Region ClearSession

export const clearSession = () => {
  localStorageUtils.removeAll();
};
// Region End ClearSession

export const prepareMessageFromParams = (
  message: string,
  params: [string, string][]
) => {
  let resultMessage = message;
  for (const [key, value] of params) {
    resultMessage = resultMessage.replaceAll("<<" + key + ">>", value);
  }
  return resultMessage;
};

export const getFormikErrorMessage = <T extends Object>(
  errors: FormikErrors<T>,
  touched: FormikTouched<T>,
  name: keyof T
) => {
  let errorMessage;
  if (touched[name] && errors[name]) {
    errorMessage = errors[name];
  }
  if (!errorMessage) {
    return "";
  }
  if (typeof errorMessage === "string") {
    return errorMessage;
  }
  return "";
};

export const hasAnyModification = <T extends Object>(
  object: T,
  objectToCompare: T
) => {
  let hasChanges = false;
  let key: keyof T;
  for (key in object) {
    if (object[key] !== objectToCompare[key]) {
      hasChanges = true;
      break;
    }
  }
  return hasChanges;
};

//generate pagination object from Api
export const generatePaginationFromApiRes = (
  objPagination: IItemPagination
) => {
  const pagination: IPaginationApi = {
    totalPages: objPagination.totalPages,
    totalItems: objPagination.totalItems,
    perPageRows: objPagination.perPageRows,
    currentPage: objPagination.currentPage,
  };
  return pagination;
};

export const resetPaginationWithPpr = (perPageRows: number) => {
  return {
    totalPages: 0,
    totalItems: 0,
    perPageRows: perPageRows,
    currentPage: 1,
  };
};

export const dateFormate = (item: string | Date): string => {
  const date = new Date(item);

  const month = MONTH_SHORT_NAME[date.getMonth()];
  let dt = date.getDate();
  const year = date.getFullYear();
  if (dt < 10) {
    dt = parseInt("0" + dt); // Parse it as an integer
  }
  return `${dt} ${month} ${year}`;
};

export const getTextContentOnly = (htmlText: string) => {
  const regex = /(<([^>]+)>)/gi;
  const result = htmlText ? htmlText.replace(regex, "") : "";
  return result;
};

export const formatTime = (timeString: string) => {
  // Parse the time string
  const [hours, minutes, seconds] = timeString.split(":").map(Number);

  // Create a Date object and set the hours, minutes, and seconds
  const time = getLocalDate();
  time.setHours(hours);
  time.setMinutes(minutes);
  time.setSeconds(seconds);

  // Format the time as "HH:MM AM/PM"
  const formattedTime = time.toLocaleString("en-US", {
    hour: "2-digit",
    minute: "numeric",
    hour12: true,
    hourCycle: "h23",
  });

  return formattedTime;
};

// add Limit functionality
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) {
    return text;
  } else {
    const truncatedLength = maxLength - 3;
    return text.substring(0, truncatedLength) + "...";
  }
};
export const encryptData = (text: string) => {
  const key = CryptoJS.SHA256(CRYPTO_CIPHER_KEY);
  const iv = CryptoJS.lib.WordArray.random(16);
  const encrypted = CryptoJS.AES.encrypt(text, key, {
    iv,
    padding: CryptoJS.pad.Pkcs7,
    mode: CryptoJS.mode.CBC,
  });
  return `${iv.toString(CryptoJS.enc.Hex)}:${encrypted.ciphertext.toString(CryptoJS.enc.Hex)}`;
};
export const decryptData = (text: string) => {
  const [ivHex, cipherHex] = text.split(":");
  const key = CryptoJS.SHA256(CRYPTO_CIPHER_KEY);
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const ciphertext = CryptoJS.enc.Hex.parse(cipherHex);
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext } as CryptoJS.lib.CipherParams,
    key,
    {
      iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC,
    },
  );
  return decrypted.toString(CryptoJS.enc.Utf8);
};

// mobile number encryption
export const maskMobileNumber = (number: string): string => {
  if (number.length < 5) return number; // Ensure number has at least 10 digits
  return number.slice(0, -4).replace(/\d/g, "*") + number.slice(-4);
};

// email encryption

export const maskEmail = (email: string): string => {
  if (typeof email !== "string" || !email.includes("@")) {
    console.error("Invalid email format");
    return "";
  }

  const [localPart, domainPart] = email.split("@");

  if (!localPart || !domainPart) {
    console.error("Invalid email format after split");
    return email;
  }

  const maskedLocalPart =
    localPart.length > 2
      ? localPart[0] +
        localPart.slice(1, -1).replace(/./g, "*") +
        localPart.slice(-1)
      : localPart.replace(/./g, "*");

  const domainParts = domainPart.split(".");
  if (domainParts.length < 2) {
    console.error("Domain part format is invalid");
    return `${maskedLocalPart}@${domainPart.replace(/./g, "*")}`;
  }

  const [domain, ...tldParts] = domainParts;
  const maskedDomain = domain.replace(/./g, "*");
  const tld = tldParts.join(".");

  return `${maskedLocalPart}@${maskedDomain}.${tld}`;
};

export const formatToCurrency = (number: number) => {
  const numberStr = number.toString().split(".");
  const integerPart = numberStr[0];
  const decimalPart = numberStr.length > 1 ? "." + numberStr[1] : "";

  let lastThreeDigits = integerPart.slice(-3);
  const otherDigits = integerPart.slice(0, -3);

  if (otherDigits !== "") {
    lastThreeDigits = "," + lastThreeDigits;
  }

  let formattedNumber =
    otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",") +
    lastThreeDigits +
    decimalPart;

  return formattedNumber;
};

export const addCurrencySign = (value: number | string) => {
  return CURRENCY_SIGN + value;
};

// strength Password

const renderAlert = (
  Component: React.ComponentType<ICAnimatedAlertProps>,
  props: ICAnimatedAlertProps
) => {
  const container = document.createElement("div");
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(
    React.createElement(Component, { ...props, onClose: () => root.unmount() })
  );

  if (props.duration !== undefined) {
    setTimeout(() => {
      root.unmount();
      document.body.removeChild(container);
    }, props.duration);
  }
};

export const sweetAlertSuccess = ({
  message,
  title,
  duration,
  noClose,
}: ISweetAlertOptions) => {
  renderAlert(ICAnimatedAlert, {
    type: "success",
    message,
    duration,
    noClose,
    title,
  });
};

export const sweetAlertError = ({
  message,
  title,
  duration,
  noClose,
}: ISweetAlertOptions) => {
  renderAlert(ICAnimatedAlert, {
    type: "error",
    message,
    duration,
    noClose,
    title,
  });
};

export function masking(
  input: string,
  regex: RegExp,
  maskChar: string = "*"
): string {
  return input.replace(regex, (match) => maskChar.repeat(match.length));
}

export const toggleStatus = <T extends IStatusValues>(
  list: T[],
  itemId: string
): T[] => {
  return list.map((item) => {
    if (item.id === itemId) {
      return {
        ...item,
        is_active:
          item.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
      };
    }
    return item;
  });
};

export const limitString = (inputString: string, maxCharacters?: number) => {
  if (maxCharacters) {
    if (
      inputString &&
      maxCharacters >= 0 &&
      inputString.length > maxCharacters
    ) {
      return inputString.substring(0, maxCharacters) + "...";
    } else {
      return inputString;
    }
  }
  return inputString;
};

const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;

  const matchVersion = (regex: RegExp) => {
    const match = regex.exec(userAgent);
    return match ? match[1] : "";
  };

  const browsers = [
    { name: "Firefox", regex: /Firefox\/([0-9.]+)/ },
    { name: "Edge", regex: /Edg\/([0-9.]+)/ },
    { name: "Opera", regex: /Opera\/([0-9.]+)|OPR\/([0-9.]+)/ },
    { name: "Chrome", regex: /Chrome\/([0-9.]+)/ },
    { name: "Safari", regex: /Version\/([0-9.]+)/, exclude: /Chrome/ },
    { name: "IE", regex: /(?:MSIE |rv:)([0-9.]+)/, include: /Trident|MSIE/ },
    { name: "Samsung", regex: /SamsungBrowser\/([0-9.]+)/ },
  ];

  for (const { name, regex, exclude, include } of browsers) {
    if (
      (userAgent.includes(name) && !exclude?.test(userAgent)) ||
      include?.test(userAgent)
    ) {
      return { browserName: name, browserVersion: matchVersion(regex) };
    }
  }

  return { browserName: "", browserVersion: "" };
};

export const getOSInfo = () => {
  const userAgent = navigator.userAgent;
  let os = OS_NAME.Unknown;
  let osVersion = "";

  // Windows NT
  if (userAgent.includes("Windows NT")) {
    os = OS_NAME.Windows;
    const match = /Windows NT ([0-9.]+)/.exec(userAgent);
    osVersion = match ? match[1] : osVersion;
  }
  // Mac OS X or Macintosh
  else if (userAgent.includes("Mac OS X") || userAgent.includes("Macintosh")) {
    os = OS_NAME.MacOS;
    const match = /Mac OS X ([0-9_]+)/.exec(userAgent);
    osVersion = match ? match[1].replace(/_/g, ".") : osVersion;
  }
  // Linux (excluding Android)
  else if (userAgent.includes("Linux") && !userAgent.includes("Android")) {
    os = OS_NAME.Linux;
  }
  // Android
  else if (userAgent.includes("Android")) {
    os = OS_NAME.Android;
    const match = /Android ([0-9.]+)/.exec(userAgent);
    osVersion = match ? match[1] : osVersion;
  }
  // iPhone, iPad, iPod (iOS)
  else if (/iPhone|iPad|iPod/.test(userAgent)) {
    os = OS_NAME.IOS;
    const match = /OS ([0-9_]+)/.exec(userAgent);
    osVersion = match ? match[1].replace(/_/g, ".") : osVersion;
  }

  // If no version found, default to 'N/A'
  osVersion = osVersion === "Unknown Version" ? "N/A" : osVersion;

  return { os, osVersion };
};

const getDeviceName = (width: number) => {
  if (width < 768) {
    return DEVICE_NAME.Mobile;
  } else if (width < 1024) {
    return DEVICE_NAME.Tablet;
  } else {
    return DEVICE_NAME.Desktop;
  }
};
export const getDeviceType = () => {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent;

  // Check for mobile or tablet devices based on userAgent
  if (/android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent)) {
    if (width <= 768) {
      return DEVICE_NAME.Mobile;
    } else if (width <= 1024) {
      return DEVICE_NAME.Tablet;
    }
  }
  // Check for desktop devices (Windows, Mac, Linux)
  else if (/Windows|Mac|Linux/i.test(userAgent)) {
    return DEVICE_NAME.Desktop;
  }

  // Default to mobile, tablet, or desktop based on window width
  return getDeviceName;
};

export const handleError = (error: any, functionName: string) => {
  sweetAlertError({
    message: error?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
  });
  const { browserName, browserVersion } = getBrowserInfo();
  const { os, osVersion } = getOSInfo();
  const deviceType = getDeviceType();
  const rawErrorPayload = {
    device_name: deviceType,
    version: "",
    browser_name: browserName,
    browser_version: browserVersion,
    os: os,
    os_version: osVersion,
    url: window.location.href,
    errorTime: new Date().toISOString(),
    error_name: error.name,
    error_message: error.message,
    error_stack: JSON.stringify(error.stack || ""),
    function_name: functionName,
  };
  const errorPayload = Object.fromEntries(
    Object.entries(rawErrorPayload).filter(([_, value]) => value !== "")
  );
  logApplicationError(errorPayload);
};

export const generateYearRange = (
  startYear: number,
  endYear: number
): IOption<number>[] => {
  const years: IOption<number>[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push({ display_value: year.toString(), data_value: year });
  }
  return years;
};

export const formatDuration = (totalDays: number) => {
  const years = Math.floor(totalDays / 360);
  const months = Math.floor((totalDays % 360) / 30);
  const days = (totalDays % 360) % 30;

  const parts = [];
  if (years > 0) parts.push(`${years} Year${years > 1 ? "s" : ""}`);
  if (months > 0) parts.push(`${months} Month${months > 1 ? "s" : ""}`);
  if (days > 0) parts.push(`${days} Day${days > 1 ? "s" : ""}`);

  return parts.join(" ");
};
